/** Per-type row actions for the Legal Calendar / Agenda (Legal plan item 5).
 *  The server tells us which outcomes fit the row (`ev.actions`); every outcome writes one timeline line. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ban, CheckCircle2, Gavel, Hourglass, Landmark, Mail, Map as MapIcon, PoundSterling, RotateCw, Send, StickyNote, TimerOff, Trash2, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { ukDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { deleteLegalEvent } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import HearingOutcomeDialog from "./petition/HearingOutcomeDialog";
import { ActionDialog, AllocateDialog, GenericHearingOutcomeDialog } from "./EventActionDialogs";

const META = {
  record_outcome:     { label: "Record outcome",       Icon: Gavel,         cls: "text-slate-800" },
  passed_no_response: { label: "Passed — no response", Icon: Hourglass,     cls: "text-amber-700" },
  response_received:  { label: "Response received",    Icon: Mail,          cls: "text-sky-700" },
  served:             { label: "Sent / served",        Icon: Send,          cls: "text-sky-700" },
  expired_no_payment: { label: "Expired — no payment", Icon: TimerOff,      cls: "text-amber-700" },
  payment_received:   { label: "Payment received",     Icon: PoundSterling, cls: "text-emerald-700" },
  done:               { label: "Done",                 Icon: CheckCircle2,  cls: "text-emerald-600" },
  chase_again:        { label: "Chase again",          Icon: RotateCw,      cls: "text-sky-700" },
  cancel:             { label: "Cancel",               Icon: Ban,           cls: "text-slate-600" },
  allocate:           { label: "Allocate to…",         Icon: UserPlus,      cls: "text-indigo-700" },
  filed_ch:           { label: "Filed at CH",          Icon: Landmark,      cls: "text-emerald-700" },
};
const LOCKED_HIDDEN = new Set(["cancel", "chase_again"]);

export function EventActions({ ev, onChanged, className = "" }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const actions = (ev.actions || []).filter((a) => !(ev.locked && !admin && LOCKED_HIDDEN.has(a)));
  const primary = actions.filter((a) => META[a]);
  const canNote = actions.includes("note");
  const canStep = actions.includes("open_step");
  const canDelete = actions.includes("delete") && admin;
  if (!primary.length && !canNote && !canStep && !canDelete) return null;

  const done = async () => { setOpen(null); await onChanged?.(); };
  const remove = async () => {
    if (!window.confirm(`Delete "${ev.label}" on ${ukDate(ev.date)}? This cannot be undone.`)) return;
    try { setBusy(true); await deleteLegalEvent(ev.event_id, false, admin); toast.success("Event deleted"); await onChanged?.(); }
    catch (e) { toast.error("Delete failed: " + (e?.response?.data?.detail || e.message)); } finally { setBusy(false); }
  };
  const openStep = () => navigate(`/legal/case/${encodeURIComponent(ev.case_id)}?source=legal&step=${encodeURIComponent(ev.map.step_id)}`);

  return (
    <div className={`flex items-center gap-1 flex-wrap ${className}`} data-testid={`lcal-actions-${ev.event_id}`}>
      {primary.map((a) => {
        const { label, Icon, cls } = META[a];
        return (
          <Button key={a} size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} onClick={() => setOpen(a)}
            title={ev.mandatory && a === "cancel" ? "Mandatory action — complete it instead" : undefined} data-testid={`lcal-act-${a}-${ev.event_id}`}>
            <Icon className={`w-3 h-3 mr-1 ${cls}`} /> {label}
          </Button>
        );
      })}
      {canNote && (
        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-600" disabled={busy} onClick={() => setOpen("note")} title="Add a note to the file timeline" data-testid={`lcal-act-note-${ev.event_id}`}>
          <StickyNote className="w-3 h-3 mr-1" /> Note
        </Button>
      )}
      {canStep && (
        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-600" onClick={openStep} title="Open this step on the Legal map" data-testid={`lcal-act-open-step-${ev.event_id}`}>
          <MapIcon className="w-3 h-3 mr-1" /> Map step
        </Button>
      )}
      {canDelete && (
        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600 hover:text-rose-700 ml-auto" onClick={remove} disabled={busy} title="Admin — delete this hand-added row" data-testid={`lcal-act-delete-${ev.event_id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
      {open === "record_outcome" && ev.outcome_mode === "petition" && (
        <HearingOutcomeDialog caseRow={{ case_id: ev.case_id, customer_name: ev.customer_name }} onClose={() => setOpen(null)} onSaved={done} />
      )}
      {open === "record_outcome" && ev.outcome_mode !== "petition" && <GenericHearingOutcomeDialog ev={ev} onClose={() => setOpen(null)} onDone={done} />}
      {open === "allocate" && <AllocateDialog ev={ev} onClose={() => setOpen(null)} onDone={done} />}
      {open && !["record_outcome", "allocate"].includes(open) && <ActionDialog ev={ev} action={open} onClose={() => setOpen(null)} onDone={done} />}
    </div>
  );
}
